import React, { useEffect, useMemo, useRef, useState } from 'react';
import Coter from './ecrans/Coter.jsx';
import Reglages from './ecrans/Reglages.jsx';
import MiseAJour from './composants/MiseAJour.jsx';
import Historique from './ecrans/Historique.jsx';
import Position from './ecrans/Position.jsx';
import { listerDevis, enregistrerDevis, majDevis } from './stockage/devis.js';
import { livreCourant, enregistrerMouvements, confirmerDevis, supprimerMouvement } from './stockage/mouvements.js';
import { livreVide } from './livre/index.js';
import { amorcerSiVide, listerProfils, enregistrerProfil } from './stockage/profils.js';
import { lireReglages, ecrireReglages, REGLAGES_DEFAUT } from './stockage/reglages.js';

/**
 * Coquille applicative et navigation.
 *
 * Les profils et les réglages sont chargés une fois ici puis passés aux écrans :
 * deux écrans qui liraient la base séparément finiraient par en afficher deux
 * états différents.
 *
 * Coter est l'écran par défaut au lancement (document 04).
 */
export default function App() {
  const [ecran, setEcran] = useState('coter');
  const zonePrincipale = useRef(null);
  // Premier rendu exclu : on ne vole pas le focus à l'ouverture de l'application.
  const premierRendu = useRef(true);
  const [reglages, setReglages] = useState(REGLAGES_DEFAUT);
  const [profils, setProfils] = useState([]);
  const [devis, setDevis] = useState([]);
  // Le livre n'est jamais persisté : il se reconstruit depuis les mouvements
  // (document 02, règle d'or). Ce qui est en mémoire ici n'est qu'un rendu.
  const [livre, setLivre] = useState(livreVide());
  const [journal, setJournal] = useState([]);
  // Devis à rejouer, transmis à Coter. Rejouer ne modifie jamais l'original :
  // enregistrer produit un nouveau devis (document 04).
  const [rejeu, setRejeu] = useState(null);
  const [pret, setPret] = useState(false);
  // « Hors-ligne - vos cotations sont enregistrées localement ». Discret : la
  // cotation n'est jamais bloquée, le serveur n'est qu'un miroir (AD-1).
  const [enLigne, setEnLigne] = useState(navigator.onLine);

  useEffect(() => {
    const maj = () => setEnLigne(navigator.onLine);
    window.addEventListener('online', maj);
    window.addEventListener('offline', maj);
    return () => {
      window.removeEventListener('online', maj);
      window.removeEventListener('offline', maj);
    };
  }, []);

  useEffect(() => {
    let vivant = true;
    (async () => {
      // Premier lancement : on pré-remplit avec des profils d'exemple plutôt
      // que de présenter une liste vide.
      const [p, r, d, l] = await Promise.all([
        amorcerSiVide(), lireReglages(), listerDevis(), livreCourant(),
      ]);
      if (!vivant) return;
      setProfils(p);
      setReglages(r);
      setDevis(d);
      setLivre(l.livre);
      setJournal(l.journal);
      setPret(true);
    })().catch(() => vivant && setPret(true));
    return () => { vivant = false; };
  }, []);

  // Après une synchro, profils et devis peuvent avoir changé des deux côtés.
  const rechargerLivre = async () => {
    const { livre: l, journal: j } = await livreCourant();
    setLivre(l);
    setJournal(j);
  };

  const rechargerDonnees = async () => {
    await rechargerLivre();
    const [p, d] = await Promise.all([listerProfils(), listerDevis()]);
    setProfils(p);
    setDevis(d);
  };

  const changerTheme = async (theme) => {
    setReglages((r) => ({ ...r, theme }));
    await ecrireReglages({ theme });
  };

  const ecrans = [
    { cle: 'coter', libelle: 'Coter', pret: true },
    { cle: 'position', libelle: 'Position', pret: true },
    { cle: 'historique', libelle: 'Historique', pret: true, compteur: devis.length },
    { cle: 'reglages', libelle: 'Réglages', pret: true },
  ];

  // Un devis est inscrit au livre si un mouvement le référence. C'est la seule
  // source qui engage réellement la comptabilité.
  const quotesInscrits = useMemo(
    () => new Set(journal.map((e) => e.mouvement.quoteId).filter(Boolean)),
    [journal],
  );

  useEffect(() => {
    if (premierRendu.current) { premierRendu.current = false; return; }
    zonePrincipale.current?.focus();
  }, [ecran]);

  return (
    <div data-theme={reglages.theme} style={{ minHeight: '100vh', background: 'var(--couleur-fond)' }}>
      <div style={{
        maxWidth: 760, margin: '0 auto', padding: 'var(--espace-4)',
        // La réserve est posée par MiseAJour tant que son bandeau est affiché.
        paddingBottom: 'calc(var(--espace-6) + var(--reserve-invite, 0px))',
      }}>
        <header style={{ marginBottom: 'var(--espace-5)' }}>
          <h1 style={{ margin: 0, fontFamily: 'var(--police-titre)', fontSize: '1.5rem', fontWeight: 700 }}>
            Corridor <span style={{ color: 'var(--couleur-emphase)' }}>FX</span>
          </h1>
          <p style={{ margin: 0, color: 'var(--couleur-encre-douce)', fontSize: '0.875rem' }}>
            Cotation de transferts EUR ↔ CFA
          </p>
        </header>

        {!enLigne && (
          <p role="status" style={{
            margin: '0 0 var(--espace-4)', padding: 'var(--espace-2) var(--espace-3)',
            border: '1px solid var(--couleur-trait-fort)', borderRadius: 'var(--rayon)',
            background: 'var(--couleur-surface)', color: 'var(--couleur-encre-douce)',
            fontSize: '0.8rem',
          }}>
            Hors-ligne - vos cotations sont enregistrées localement.
          </p>
        )}

        {/* `flexWrap` : à 320 px - un iPhone SE - les trois onglets dépassaient
            de 8 px et faisaient défiler la page horizontalement. */}
        <nav aria-label="Écrans" style={{
          display: 'flex', gap: 'var(--espace-2)', flexWrap: 'wrap',
          marginBottom: 'var(--espace-5)',
        }}>
          {ecrans.map((e) => (
            <button
              key={e.cle}
              type="button"
              onClick={() => e.pret && setEcran(e.cle)}
              disabled={!e.pret}
              aria-current={ecran === e.cle ? 'page' : undefined}
              title={e.pret ? undefined : `À construire - ${e.lot}`}
              style={{
                padding: 'var(--espace-2) var(--espace-4)',
                border: '1px solid var(--couleur-trait-fort)',
                borderRadius: 'var(--rayon)',
                background: ecran === e.cle ? 'var(--couleur-encre)' : 'transparent',
                color: ecran === e.cle ? 'var(--couleur-fond)'
                  : e.pret ? 'var(--couleur-encre)' : 'var(--couleur-encre-douce)',
                opacity: e.pret ? 1 : 0.5,
                cursor: e.pret ? 'pointer' : 'not-allowed',
              }}
            >
              {e.libelle}{e.compteur ? ` (${e.compteur})` : ''}
            </button>
          ))}
        </nav>

        {/* Changer d'écran ne recharge pas la page : sans ce déplacement de
            focus, un lecteur d'écran reste sur le bouton de navigation et ne
            dit rien du contenu qui vient d'apparaître. */}
        <main ref={zonePrincipale} tabIndex={-1} style={{ outline: 'none' }}>
          {!pret && <p style={{ color: 'var(--couleur-encre-douce)' }}>Chargement…</p>}
          {pret && ecran === 'coter' && (
            <Coter
              profils={profils}
              rejeu={rejeu}
              onEnregistrerProfil={async (donnees) => {
                const enregistre = await enregistrerProfil(donnees);
                setProfils(await listerProfils());
                return enregistre;
              }}
              // Confirmer, c'est déclarer que l'opération s'est faite. Le
              // devis reste figé (AD-5) ; ce sont les mouvements engendrés qui
              // portent le réel (AD-10).
              onConfirmer={async (payload, corridor) => {
                const devisCree = await enregistrerDevis(payload);
                await confirmerDevis(devisCree, corridor);
                await majDevis(devisCree, { status: 'regle' });
                setDevis(await listerDevis());
                await rechargerLivre();
              }}
              onEnregistrer={async (payload) => {
                await enregistrerDevis(payload);
                setDevis(await listerDevis());
              }}
            />
          )}
          {pret && ecran === 'position' && (
            <Position
              livre={livre}
              journal={journal}
              reglages={reglages}
              onMouvement={async ({ mouvement, note }) => {
                await enregistrerMouvements([mouvement], { note });
                await rechargerLivre();
              }}
              onSupprimer={async (id) => {
                await supprimerMouvement(id);
                await rechargerLivre();
              }}
            />
          )}
          {pret && ecran === 'historique' && (
            <Historique
              devis={devis}
              profils={profils}
              onDevisChanges={setDevis}
              onRejouer={(d) => { setRejeu(d); setEcran('coter'); }}
              // L'ensemble des devis déjà inscrits au livre, déduit des
              // mouvements et non des statuts : un statut se change d'un clic,
              // un mouvement engage la position.
              quotesInscrits={quotesInscrits}
              onConfirmer={async (d, corridor) => {
                await confirmerDevis(d, corridor);
                await majDevis(d, { status: 'regle' });
                setDevis(await listerDevis());
                await rechargerLivre();
              }}
            />
          )}
          {pret && ecran === 'reglages' && (
            <Reglages
              profils={profils}
              onProfilsChanges={setProfils}
              theme={reglages.theme}
              onTheme={changerTheme}
              reglages={reglages}
              onReglages={setReglages}
              onDonneesChangees={rechargerDonnees}
            />
          )}
        </main>

        <footer style={{
          marginTop: 'var(--espace-6)', paddingTop: 'var(--espace-3)',
          borderTop: '1px solid var(--couleur-trait)',
          fontSize: '0.8rem', color: 'var(--couleur-encre-douce)',
        }}>
          Outil de cotation et de suivi. Ce n’est pas un service de paiement.
          {' · '}front {import.meta.env.VITE_APP_VERSION ?? 'dev'}

          {/* `rel` complet : `noopener` empêche la page ouverte d'accéder à
              `window.opener`, `noreferrer` évite de lui transmettre l'adresse
              de l'application. Deux mots pour une cible externe. */}
          <div style={{ marginTop: 'var(--espace-2)' }}>
            Conçu par{' '}
            <a
              href="https://acloud.digital/"
              target="_blank"
              rel="noopener noreferrer"
              // 24 px de haut : le critère 2.5.8 tolère un lien pris dans une
              // phrase, mais un pied de page se vise au pouce et la tolérance
              // ne rend pas le geste plus facile.
              style={{
                display: 'inline-flex', alignItems: 'center', minHeight: 24,
                color: 'var(--couleur-accent)', fontWeight: 600, textDecoration: 'none',
              }}
            >
              <span className="chiffre" style={{ fontWeight: 400, opacity: 0.75 }}>./</span>aCloud.Digital
            </a>
          </div>
        </footer>
      </div>

      {/* Hors du conteneur centré : l'invite est fixée au bas de la fenêtre,
          la mettre dans le flux la ferait défiler avec la page. */}
      <MiseAJour />
    </div>
  );
}
